import type { SectionId } from './reportEnums.constants';
import { SECTION_IDS } from './reportEnums.constants';

/** Default authorable callout copy for a report section. */
export interface SectionDefaultCallout {
  /** Callout heading shown above the body (e.g. "Why the denominator matters"). */
  title: string;
  /** Callout body paragraph. */
  body: string;
}

/** Studio default callout title + body keyed by section id. */
export const SECTION_DEFAULT_CALLOUTS: Partial<
  Record<SectionId, SectionDefaultCallout>
> = {
  'engagement-story': {
    title: 'Why the denominator matters',
    body: 'Engagement rate and conversion rate summarize how members move from eligibility to an active guide relationship.',
  },
  'vbp-engagement': {
    title: 'Why the denominator matters',
    body: 'By design, we don\u2019t outreach to every eligible member. We focus on the subset most likely to drive ROI for the plan \u2014 typically the highest-need members where a community guide can have the greatest impact.',
  },
  'bh-medical-diagnoses': {
    title: 'Why the denominator matters',
    body: 'These diagnoses reflect only the engaged members with six months of claims runout. As more members reach claims maturity, the denominator grows and these figures will continue to climb.',
  },
  'harp-enrollment': {
    title: 'How we track HARP status',
    body: 'HARP status is updated by guides after each member conversation. Members move from Undecided to In Progress once they agree to enroll, and to Enrolled in HARP after enrollment is confirmed.',
  },
  'gap-closures': {
    title: 'How we track gap closures',
    body: 'Guides track preventive screening progress for each member. Statuses move from In Progress to Scheduled once an appointment is booked, and to Completed after the screening is confirmed.',
  },
  'home-visits': {
    title: 'How we track home visits',
    body: 'Guides coordinate home visits with MANNA and Scene partners. Members move from In Program to Completed once a home visit is confirmed, or to Refused if the member declines the visit.',
  },
  'quality-measure-projections': {
    title: 'How we project',
    body: 'Rates come from measurement-period warehouse metrics for blood pressure gap closure, follow-up after ED or hospitalization (FUA, FUH, FUM), and VBP engagement. When both verified 7-day and 30-day measures are published, each appears as its own projection card.',
  },
};

/**
 * Returns true when the section id is a known library section.
 * @param sectionId - Section library id string.
 */
export const isReportSectionId = (sectionId: string): sectionId is SectionId =>
  (SECTION_IDS as readonly string[]).includes(sectionId);

/**
 * Returns Studio default callout copy for a section, if configured.
 * @param sectionId - Section library id.
 * @returns Default title/body or null when the section has no default callout.
 */
export const sectionDefaultCalloutForSection = (
  sectionId: string,
): SectionDefaultCallout | null => {
  if (!isReportSectionId(sectionId)) {
    return null;
  }
  return SECTION_DEFAULT_CALLOUTS[sectionId] ?? null;
};

/**
 * Resolves the callout title for display, falling back to section defaults.
 * @param sectionId - Section library id.
 * @param storedTitle - Persisted `calloutTitle` from the report section row.
 * @returns Stored title, section default title, or null.
 */
export const resolveSectionCalloutTitle = (
  sectionId: string,
  storedTitle: string | null | undefined,
): string | null => {
  const trimmed = storedTitle?.trim();
  if (trimmed) {
    return trimmed;
  }
  return sectionDefaultCalloutForSection(sectionId)?.title ?? null;
};

/**
 * Resolves the callout body for display, falling back to section defaults.
 * @param sectionId - Section library id.
 * @param storedBody - Persisted `callout` from the report section row.
 * @returns Stored body, section default body, or null.
 */
export const resolveSectionCalloutBody = (
  sectionId: string,
  storedBody: string | null | undefined,
): string | null => {
  const trimmed = storedBody?.trim();
  if (trimmed) {
    return trimmed;
  }
  return sectionDefaultCalloutForSection(sectionId)?.body ?? null;
};

/**
 * Returns true when a section row should show a callout (stored or default copy).
 * @param sectionId - Section library id.
 * @param storedBody - Persisted callout body.
 * @returns Whether resolved callout body is non-empty.
 */
export const sectionCalloutHasDisplayContent = (
  sectionId: string,
  storedBody: string | null | undefined,
): boolean => {
  const body = resolveSectionCalloutBody(sectionId, storedBody);
  return Boolean(body && body.trim().length > 0);
};
